/**
 * The Web — seed data + helpers
 *
 * Models the concept graph for an ad-creative intelligence system, per
 * outline.md §5. Three node types:
 *   concept  — the high-level idea (e.g. "Wife Notices")
 *   variation — a specific creative under that concept
 *   hook     — a reusable opening that travels across concepts
 *
 * Seed data is intentionally rich so the UI looks alive on first load,
 * before live Meta API ingestion exists.
 */

export const FUNNEL_STAGES = [
  { key: "tof", label: "TOF · Cold", color: "#22d3ee" },
  { key: "mof", label: "MOF · Warm", color: "#f59e0b" },
  { key: "bof", label: "BOF · Hot",  color: "#ec4899" },
];

export const AWARENESS_LEVELS = [
  { key: "unaware",       label: "Unaware" },
  { key: "problem",       label: "Problem-Aware" },
  { key: "solution",      label: "Solution-Aware" },
  { key: "product",       label: "Product-Aware" },
  { key: "most",          label: "Most Aware" },
];

export const ANGLES = [
  { key: "fear",            label: "Fear" },
  { key: "embarrassment",   label: "Embarrassment" },
  { key: "partner_approval",label: "Partner approval" },
  { key: "identity",        label: "Masculine identity" },
  { key: "authority",       label: "Authority" },
  { key: "humor",           label: "Humor" },
  { key: "comparison",      label: "Comparison" },
  { key: "transformation",  label: "Transformation" },
  { key: "ugc",             label: "UGC / testimonial" },
  { key: "educational",     label: "Educational" },
];

export const FORMATS = [
  { key: "static_ugc",      label: "Native UGC selfie" },
  { key: "advertorial",     label: "Editorial / advertorial" },
  { key: "founder_pov",     label: "Founder POV" },
  { key: "premium_hero",    label: "Premium product hero" },
  { key: "before_after",    label: "Sticky-note before/after" },
  { key: "meme",            label: "Meme / cultural" },
  { key: "hand_grip",       label: "Hand-grip product" },
  { key: "comparison_side", label: "Side-by-side comparison" },
  { key: "carousel",        label: "Carousel" },
  { key: "video_hook",      label: "Video · hook" },
  { key: "video_demo",      label: "Video · demo" },
  { key: "video_skit",      label: "Video · skit" },
];

export const STATUSES = {
  active:     { label: "Active",     color: "#a78bfa" },
  scaling:    { label: "Scaling",    color: "#a78bfa" },
  paused:     { label: "Paused",     color: "#94a3b8" },
  killed:     { label: "Killed",     color: "#475569" },
  unexplored: { label: "Unexplored", color: "#1f2937" },
};

export const ZOOM_LEVELS = [
  { key: 1, label: "L1 · Niche",    sub: "Married men 35–55, fear-of-cutting" },
  { key: 2, label: "L2 · Category", sub: "Groin grooming, sensitive-skin" },
  { key: 3, label: "L3 · Vertical", sub: "Men's premium grooming + body care" },
  { key: 4, label: "L4 · Identity", sub: "Premium-care American man" },
];

/* -------------------------------------------------------------------- */
/*  Concepts                                                            */
/* -------------------------------------------------------------------- */

const C = (id, overrides) => ({
  id,
  type: "concept",
  level: 1,
  funnel: "tof",
  awareness: "problem",
  sophistication: 4,
  angle: "fear",
  format: "static_ugc",
  status: "active",
  spend: 8000,
  roas: 2.4,
  ctr: 1.6,
  hookRate: 0.28,
  confidence: 0.7,
  scaling: false,
  thesis: "",
  ...overrides,
});

export const CONCEPTS = [
  C("c_wife_notices", {
    label: "Wife Notices",
    angle: "partner_approval",
    awareness: "solution",
    spend: 64000, roas: 4.2, ctr: 2.4, hookRate: 0.41, confidence: 0.95,
    scaling: true, status: "scaling",
    thesis:
      "Reframes grooming as relational care, not vanity. Wife-POV opens the loop on a private outcome the buyer already wants but won't admit.",
  }),
  C("c_founder_letter", {
    label: "Founder Letter",
    angle: "authority",
    awareness: "problem",
    sophistication: 3,
    format: "founder_pov",
    spend: 32000, roas: 3.6, ctr: 2.0, hookRate: 0.36, confidence: 0.9,
    status: "active",
    thesis:
      "Authority + mechanism reveal. Founder explains why every drugstore tool fails skin below the belt. Pre-sells the premium price.",
  }),
  C("c_specificity_14d", {
    label: "Specificity · 14 days",
    angle: "transformation",
    awareness: "product",
    sophistication: 4,
    format: "before_after",
    spend: 22000, roas: 3.1, ctr: 1.9, hookRate: 0.31, confidence: 0.86,
    status: "active",
    thesis:
      "Concrete window beats vague promise. Stops scroll because '14 days' cashes a check most buyers can verify themselves.",
  }),
  C("c_authority_doctor", {
    label: "Authority · Doctor",
    angle: "authority",
    awareness: "problem",
    format: "video_hook",
    spend: 18500, roas: 2.9, ctr: 2.1, hookRate: 0.34, confidence: 0.81,
    status: "active",
  }),
  C("c_fear_cuts", {
    label: "Fear · Cuts & Irritation",
    angle: "fear",
    awareness: "problem",
    sophistication: 2,
    format: "static_ugc",
    spend: 41000, roas: 2.7, ctr: 1.7, hookRate: 0.39, confidence: 0.92,
    status: "active",
  }),
  C("c_embarrassment_partner", {
    label: "Embarrassment · Partner saw",
    angle: "embarrassment",
    awareness: "problem",
    format: "static_ugc",
    spend: 14000, roas: 2.1, ctr: 1.4, hookRate: 0.33, confidence: 0.74,
    status: "paused",
  }),
  C("c_humor_locker", {
    label: "Humor · Locker-room",
    angle: "humor",
    awareness: "solution",
    format: "video_skit",
    spend: 9000, roas: 1.4, ctr: 1.1, hookRate: 0.18, confidence: 0.6,
    status: "killed",
  }),
  C("c_comparison_razor", {
    label: "Comparison vs. Razor",
    angle: "comparison",
    awareness: "solution",
    sophistication: 3,
    format: "comparison_side",
    spend: 11000, roas: 2.6, ctr: 1.8, hookRate: 0.29, confidence: 0.78,
    status: "active",
  }),
  C("c_listicle_3reasons", {
    label: "Listicle · 3 Reasons",
    angle: "educational",
    awareness: "problem",
    format: "advertorial",
    spend: 8000, roas: 3.2, ctr: 2.6, hookRate: 0.30, confidence: 0.81,
    status: "active",
  }),
  C("c_ugc_real_guy", {
    label: "UGC · Real Guy",
    angle: "ugc",
    awareness: "solution",
    format: "video_hook",
    spend: 27000, roas: 3.4, ctr: 2.3, hookRate: 0.40, confidence: 0.88,
    status: "active",
  }),
  C("c_identity_provider", {
    label: "Identity · Provider",
    angle: "identity",
    awareness: "most",
    sophistication: 5,
    format: "premium_hero",
    spend: 6500, roas: 1.8, ctr: 1.3, hookRate: 0.22, confidence: 0.55,
    status: "paused",
  }),
  C("c_premium_hero_gold", {
    label: "Premium Hero · Gold",
    angle: "identity",
    awareness: "most",
    sophistication: 5,
    format: "premium_hero",
    spend: 4200, roas: 2.1, ctr: 1.5, hookRate: 0.24, confidence: 0.62,
    status: "active",
  }),

  /* — Unexplored, for the gaps overlay — */
  C("u_authority_problem", {
    level: 2, label: "Authority × Problem-Aware × Founder",
    angle: "authority", awareness: "problem", format: "founder_pov",
    spend: 0, roas: 0, ctr: 0, hookRate: 0, confidence: 0,
    status: "unexplored",
    thesis:
      "Customer language gap: 47 reviews use the phrase 'finally not afraid' — 0 of our ads do.",
  }),
  C("u_mechanism_reveal", {
    level: 2, label: "Mechanism Reveal · Why razors fail down there",
    angle: "educational", awareness: "solution", format: "advertorial",
    spend: 0, roas: 0, ctr: 0, hookRate: 0, confidence: 0,
    status: "unexplored",
    thesis: "2 competitors operating here. We've never tested.",
  }),
  C("u_quiz_path", {
    level: 1, label: "Quiz · What's your skin tier?",
    angle: "educational", awareness: "problem", format: "advertorial",
    spend: 0, roas: 0, ctr: 0, hookRate: 0, confidence: 0,
    status: "unexplored",
  }),

  /* — Adjacent levels (greyed) — */
  C("c_adj_face", {
    level: 2, label: "Face shaving · Sensitive",
    angle: "fear", format: "static_ugc",
    status: "unexplored",
  }),
  C("c_adj_skin", {
    level: 3, label: "Skincare · Post-shave",
    angle: "transformation", format: "premium_hero",
    status: "unexplored",
  }),
  C("c_adj_supps", {
    level: 4, label: "Supplements · Testosterone",
    angle: "identity", format: "advertorial",
    status: "unexplored",
  }),
];

/* -------------------------------------------------------------------- */
/*  Variations                                                          */
/* -------------------------------------------------------------------- */

const V = (id, parent, overrides) => ({
  id,
  type: "variation",
  parent,
  level: 1,
  funnel: "tof",
  awareness: "solution",
  status: "active",
  format: "static_ugc",
  spend: 4000,
  roas: 2.4,
  ctr: 1.6,
  hookRate: 0.27,
  confidence: 0.7,
  scaling: false,
  ...overrides,
});

export const VARIATIONS = [
  // Wife Notices lineage
  V("v_wn_v1",  "c_wife_notices", { label: "v1 · text screenshot",         spend: 4000,  roas: 1.8, status: "killed" }),
  V("v_wn_v2",  "c_wife_notices", { label: "v2 · text + man hand",         spend: 12000, roas: 3.1, status: "active",   confidence: 0.86 }),
  V("v_wn_v2a", "c_wife_notices", { label: "v2a · diff hand",              spend: 8000,  roas: 2.9, status: "active",   confidence: 0.78, parent: "v_wn_v2" }),
  V("v_wn_v2b", "c_wife_notices", { label: "v2b · CTA change",             spend: 3000,  roas: 1.4, status: "killed",   parent: "v_wn_v2" }),
  V("v_wn_v2c", "c_wife_notices", { label: "v2c · headline craft",         spend: 22000, roas: 4.6, status: "scaling",  scaling: true, confidence: 0.96, parent: "v_wn_v2" }),
  V("v_wn_v3",  "c_wife_notices", { label: "v3 · UGC reframe",             spend: 6000,  roas: 2.4, status: "active",   format: "video_hook" }),

  // Founder Letter
  V("v_fl_v1", "c_founder_letter", { label: "v1 · 'Why I built this'",     spend: 11000, roas: 3.4, status: "active",   format: "founder_pov", confidence: 0.84 }),
  V("v_fl_v2", "c_founder_letter", { label: "v2 · receipts shot",          spend: 8000,  roas: 3.1, status: "active",   format: "founder_pov", confidence: 0.81 }),
  V("v_fl_v3", "c_founder_letter", { label: "v3 · 14-day claim",           spend: 13000, roas: 4.0, status: "scaling",  scaling: true,         format: "founder_pov", confidence: 0.91 }),

  // Specificity 14 days
  V("v_sp_v1", "c_specificity_14d", { label: "v1 · sticky-note B/A",       spend: 9000,  roas: 3.4, status: "active",   format: "before_after", confidence: 0.86 }),
  V("v_sp_v2", "c_specificity_14d", { label: "v2 · split-screen B/A",      spend: 5000,  roas: 2.6, status: "paused",   format: "comparison_side" }),
  V("v_sp_v3", "c_specificity_14d", { label: "v3 · UGC selfie B/A",        spend: 8000,  roas: 2.9, status: "active",   format: "static_ugc" }),

  // Authority Doctor
  V("v_ad_v1", "c_authority_doctor", { label: "v1 · 'As a derm…'",         spend: 7500,  roas: 2.6, status: "active",   format: "video_hook" }),
  V("v_ad_v2", "c_authority_doctor", { label: "v2 · clinic backdrop",      spend: 11000, roas: 3.0, status: "active",   format: "video_hook", confidence: 0.85 }),

  // Fear cuts
  V("v_fc_v1", "c_fear_cuts", { label: "v1 · close-up nick",               spend: 14000, roas: 2.5, status: "active" }),
  V("v_fc_v2", "c_fear_cuts", { label: "v2 · 'I bled' headline",           spend: 17000, roas: 2.9, status: "active",   confidence: 0.88 }),
  V("v_fc_v3", "c_fear_cuts", { label: "v3 · razor vs trimmer split",      spend: 10000, roas: 2.4, status: "active",   format: "comparison_side" }),

  // UGC Real Guy
  V("v_rg_v1", "c_ugc_real_guy", { label: "v1 · bathroom selfie",          spend: 9000,  roas: 3.0, status: "active" }),
  V("v_rg_v2", "c_ugc_real_guy", { label: "v2 · post-shower POV",          spend: 12000, roas: 3.6, status: "scaling", scaling: true, confidence: 0.9 }),
  V("v_rg_v3", "c_ugc_real_guy", { label: "v3 · gym-bag haul",             spend: 6000,  roas: 2.8, status: "active" }),

  // Listicle 3 Reasons
  V("v_li_v1", "c_listicle_3reasons", { label: "v1 · 3 reasons men quit",  spend: 5000,  roas: 3.0, status: "active",   format: "advertorial" }),
  V("v_li_v2", "c_listicle_3reasons", { label: "v2 · derm + quote stack",  spend: 3000,  roas: 3.4, status: "active",   format: "advertorial", confidence: 0.84 }),

  // Comparison
  V("v_cr_v1", "c_comparison_razor", { label: "v1 · grid comparison",      spend: 6000,  roas: 2.4, status: "active",   format: "comparison_side" }),
  V("v_cr_v2", "c_comparison_razor", { label: "v2 · receipt overlay",      spend: 5000,  roas: 2.7, status: "active",   format: "static_ugc" }),
];

/* -------------------------------------------------------------------- */
/*  Hooks                                                               */
/* -------------------------------------------------------------------- */

const H = (id, label, archetype, reuses, winRate) => ({
  id, type: "hook", label, archetype, reuses, winRate,
});

export const HOOKS = [
  H("h_wife_pov",     "\"My wife noticed at the fridge…\"",      "wife_pov",            7, 0.71),
  H("h_specificity",  "\"In 14 days you'll forget about it.\"",  "specificity",         5, 0.80),
  H("h_authority",    "\"As a derm with 12 years…\"",            "authority",           4, 0.50),
  H("h_negative",     "\"Stop using a razor down there.\"",      "negative_frame",      6, 0.66),
  H("h_call_out",     "\"If you're a guy over 35…\"",            "call_out",            5, 0.60),
  H("h_insider",      "\"What men don't tell you about…\"",      "insider_revelation",  3, 0.66),
];

/* -------------------------------------------------------------------- */
/*  Edges                                                               */
/* -------------------------------------------------------------------- */
/*  parent           — variation derives from parent (solid)            */
/*  mutation         — explicit iteration of a sibling (dashed)         */
/*  cross_pollination — same hook reused across concepts (dotted, gold)  */

export const EDGES = [
  // Wife Notices tree
  { source: "c_wife_notices", target: "v_wn_v1",  type: "parent" },
  { source: "c_wife_notices", target: "v_wn_v2",  type: "parent" },
  { source: "c_wife_notices", target: "v_wn_v3",  type: "parent" },
  { source: "v_wn_v2",        target: "v_wn_v2a", type: "mutation" },
  { source: "v_wn_v2",        target: "v_wn_v2b", type: "mutation" },
  { source: "v_wn_v2",        target: "v_wn_v2c", type: "mutation" },

  // Founder Letter tree
  { source: "c_founder_letter", target: "v_fl_v1", type: "parent" },
  { source: "c_founder_letter", target: "v_fl_v2", type: "parent" },
  { source: "c_founder_letter", target: "v_fl_v3", type: "parent" },

  // Specificity tree
  { source: "c_specificity_14d", target: "v_sp_v1", type: "parent" },
  { source: "c_specificity_14d", target: "v_sp_v2", type: "parent" },
  { source: "c_specificity_14d", target: "v_sp_v3", type: "parent" },

  // Authority tree
  { source: "c_authority_doctor", target: "v_ad_v1", type: "parent" },
  { source: "c_authority_doctor", target: "v_ad_v2", type: "parent" },

  // Fear cuts tree
  { source: "c_fear_cuts", target: "v_fc_v1", type: "parent" },
  { source: "c_fear_cuts", target: "v_fc_v2", type: "parent" },
  { source: "c_fear_cuts", target: "v_fc_v3", type: "parent" },

  // UGC tree
  { source: "c_ugc_real_guy", target: "v_rg_v1", type: "parent" },
  { source: "c_ugc_real_guy", target: "v_rg_v2", type: "parent" },
  { source: "c_ugc_real_guy", target: "v_rg_v3", type: "parent" },

  // Listicle tree
  { source: "c_listicle_3reasons", target: "v_li_v1", type: "parent" },
  { source: "c_listicle_3reasons", target: "v_li_v2", type: "parent" },

  // Comparison tree
  { source: "c_comparison_razor", target: "v_cr_v1", type: "parent" },
  { source: "c_comparison_razor", target: "v_cr_v2", type: "parent" },

  // Hooks → grafted onto creatives (cross-pollination)
  { source: "h_wife_pov",     target: "v_wn_v2c",  type: "cross_pollination" },
  { source: "h_wife_pov",     target: "v_wn_v3",   type: "cross_pollination" },
  { source: "h_specificity",  target: "v_fl_v3",   type: "cross_pollination" },
  { source: "h_specificity",  target: "v_sp_v1",   type: "cross_pollination" },
  { source: "h_specificity",  target: "v_li_v2",   type: "cross_pollination" },
  { source: "h_authority",    target: "v_ad_v2",   type: "cross_pollination" },
  { source: "h_authority",    target: "v_li_v2",   type: "cross_pollination" },
  { source: "h_negative",     target: "v_fc_v2",   type: "cross_pollination" },
  { source: "h_negative",     target: "v_cr_v2",   type: "cross_pollination" },
  { source: "h_call_out",     target: "v_rg_v2",   type: "cross_pollination" },
  { source: "h_call_out",     target: "v_fl_v1",   type: "cross_pollination" },
  { source: "h_insider",      target: "v_li_v1",   type: "cross_pollination" },

  // Mutation cross-link: founder learning grafted into wife
  { source: "v_fl_v3",        target: "v_wn_v2c",  type: "mutation" },
];

/* -------------------------------------------------------------------- */
/*  Daily recommendation cards (outline §8.1)                           */
/* -------------------------------------------------------------------- */

export const DAILY_CARDS = [
  {
    id: "card_scale_wn",
    kind: "scale",
    icon: "🔥",
    title: "Wife Notices · v2c crossed stat-sig",
    body: "ROAS 4.6 over $22k spend — recommend scaling to $500/day cap.",
    nodeId: "v_wn_v2c",
    actions: ["Scale", "Defer"],
  },
  {
    id: "card_fatigue_fl",
    kind: "fatigue",
    icon: "⚠",
    title: "Founder · v1 predicted to fatigue in 3 days",
    body: "CTR down 18% vs. peak. Frequency at 4.2. Generate variation now.",
    nodeId: "v_fl_v1",
    actions: ["Brief", "Defer"],
  },
  {
    id: "card_gap_authority",
    kind: "gap",
    icon: "💡",
    title: "Untested: Authority × Problem-Aware × Founder",
    body: "47 reviews use 'finally not afraid' — 0 ads. 2 competitors active here.",
    nodeId: "u_authority_problem",
    actions: ["Brief", "Dismiss"],
  },
  {
    id: "card_kill_humor",
    kind: "kill",
    icon: "🪦",
    title: "Locker-room humor underperforming 7d at $9k",
    body: "Below kill threshold for 7 consecutive days. Recommend pause.",
    nodeId: "c_humor_locker",
    actions: ["Kill", "Defer"],
  },
  {
    id: "card_transfer_pmax",
    kind: "transfer",
    icon: "📈",
    title: "Wife Notices · high-probability PMAX transfer",
    body: "Top Meta winner shares 4 traits with proven Google PMAX winners.",
    nodeId: "c_wife_notices",
    actions: ["Brief", "Dismiss"],
  },
  {
    id: "card_revive_hook",
    kind: "revive",
    icon: "🎯",
    title: "Graft 'specificity' hook onto Embarrassment concept",
    body: "Hook win rate 80%. Concept dead visual but proven angle.",
    nodeId: "h_specificity",
    actions: ["Brief", "Defer"],
  },
];

/* -------------------------------------------------------------------- */
/*  Selectors                                                           */
/* -------------------------------------------------------------------- */

export const ALL_NODES = () => [...CONCEPTS, ...VARIATIONS, ...HOOKS];

export const findNode = (id) => ALL_NODES().find((n) => n.id === id);

export const getChildren = (id) =>
  EDGES.filter((e) => e.source === id && e.type === "parent")
    .map((e) => findNode(e.target))
    .filter(Boolean);

export const getParent = (id) => {
  const e = EDGES.find((edge) => edge.target === id && edge.type === "parent");
  return e ? findNode(e.source) : null;
};

export const getRelatedHooks = (variationId) =>
  EDGES.filter(
    (e) => e.target === variationId && e.type === "cross_pollination",
  )
    .map((e) => findNode(e.source))
    .filter(Boolean);

export const summarize = () => {
  const nodes = ALL_NODES();
  const totalSpend = nodes.reduce((acc, n) => acc + (n.spend || 0), 0);
  const active = nodes.filter((n) => n.status === "active" || n.status === "scaling").length;
  const scaling = nodes.filter((n) => n.scaling).length;
  const killed = nodes.filter((n) => n.status === "killed").length;
  const gaps = nodes.filter((n) => n.status === "unexplored").length;
  const winners = nodes.filter((n) => (n.roas || 0) >= 3 && n.status !== "unexplored").length;
  return { totalSpend, active, scaling, killed, gaps, winners, total: nodes.length };
};
